import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { EyeOff, RotateCcw, Trash2 } from 'lucide-react';
import type { CrmAccountListItem, CrmLeadDetail } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';
import { parseDecimal } from '@/utils/currency';
import { leadCapabilities } from './leads/leadCapabilities';

const NEW_ACCOUNT = 'new';

interface Props {
  nip: string;
  leadId: string | null;
  /** Company admin / superadmin — decides whether "Delete" is offered at all. */
  canDelete: boolean;
  /**
   * Bumped by the queue after IT hid or restored this lead, so the sheet
   * re-reads the detail it is showing (status badge, reason block, buttons).
   */
  refreshKey: number;
  onClose: () => void;
  onChanged: () => void;
  onHide: (id: string) => void;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-words">{value}</span>
    </div>
  );
}

/**
 * Read side of a lead plus the conversion form. The raw payload is shown
 * verbatim rather than prettified into fields: it is the evidence copy, and
 * the whole point is being able to see exactly what the sender posted,
 * including the fields the CRM does not model.
 *
 * Hide/restore/delete are NOT implemented here: the sheet only reports the
 * intent upwards. The queue owns those flows (one confirmation dialog serves
 * the row menu, the bulk bar and this sheet), which is also why the sheet
 * takes `canDelete` and `refreshKey` instead of resolving permissions and
 * refreshing itself.
 */
export function LeadDetailSheet({
  nip,
  leadId,
  canDelete,
  refreshKey,
  onClose,
  onChanged,
  onHide,
  onRestore,
  onDelete,
}: Props) {
  const { getLead, convertLead, listAccounts } = useCrmApi();

  const [lead, setLead] = useState<CrmLeadDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<CrmAccountListItem[]>([]);
  const [accountId, setAccountId] = useState<string>(NEW_ACCOUNT);
  const [accountName, setAccountName] = useState('');
  const [createContact, setCreateContact] = useState(true);
  const [createDeal, setCreateDeal] = useState(true);
  const [dealName, setDealName] = useState('');
  const [dealValue, setDealValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [detail, accountPage] = await Promise.all([
        getLead(nip, leadId),
        listAccounts(nip, { pageSize: 100 }),
      ]);
      setLead(detail);
      setAccounts(accountPage.items);
      setAccountId(detail.accountId ?? NEW_ACCOUNT);
      setAccountName(
        detail.companyName ?? [detail.firstName, detail.lastName].filter(Boolean).join(' '),
      );
      setDealName(detail.companyName ? `${detail.companyName} — zapytanie` : 'Nowe zapytanie');
      setDealValue('');
    } catch {
      toast.error(t('errors.loadingFailed'));
      // A deep link to a deleted/forbidden lead must not leave an endless
      // skeleton open (closing also clears the `?lead=` param).
      onClose();
    } finally {
      setLoading(false);
    }
  }, [nip, leadId, getLead, listAccounts, onClose]);

  useEffect(() => {
    if (leadId) load();
    else setLead(null);
    // `refreshKey` is the queue saying "I changed this lead" — without it the
    // sheet would keep showing the pre-hide status until it is reopened.
  }, [leadId, load, refreshKey]);

  const handleConvert = async () => {
    if (!lead) return;
    setSubmitting(true);
    try {
      await convertLead(nip, lead.id, {
        ...(accountId !== NEW_ACCOUNT ? { accountId } : { newAccountName: accountName.trim() }),
        createContact,
        ...(createDeal
          ? {
              deal: {
                name: dealName.trim() || accountName.trim(),
                valueNet: parseDecimal(dealValue),
              },
            }
          : {}),
      });
      toast.success(t('crm.leads.converted'));
      onChanged();
      onClose();
    } catch (error) {
      toast.error((error as Error)?.message || t('errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const actions = lead ? leadCapabilities(lead.status, canDelete) : null;

  return (
    <Sheet open={!!leadId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('crm.leads.detail.title')}</SheetTitle>
          <SheetDescription>
            {lead ? `${lead.sourceName} · ${dayjs(lead.receivedAt).format('DD.MM.YYYY HH:mm')}` : ''}
          </SheetDescription>
        </SheetHeader>

        {loading || !lead ? (
          <div className="space-y-3 py-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-4 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div>
              <Field
                label={t('crm.leads.columns.contact')}
                value={[lead.firstName, lead.lastName].filter(Boolean).join(' ')}
              />
              <Field label="E-mail" value={lead.email} />
              <Field label="Telefon" value={lead.phone} />
              <Field label={t('crm.leads.columns.company')} value={lead.companyName} />
              <Field label="NIP" value={lead.nip} />
              <Field label={t('crm.leads.detail.sourceUrl')} value={lead.sourceUrl} />
              <Field label={t('crm.leads.detail.externalId')} value={lead.externalId} />
              <Field
                label={t('crm.leads.detail.consent')}
                value={lead.consentMarketing === null ? null : lead.consentMarketing ? 'tak' : 'nie'}
              />
              <Field
                label={t('crm.leads.detail.utm')}
                value={lead.utm ? JSON.stringify(lead.utm) : null}
              />
            </div>

            {lead.message ? (
              <div>
                <Label className="text-muted-foreground text-xs uppercase">
                  {t('crm.leads.columns.message')}
                </Label>
                <p className="mt-1 whitespace-pre-wrap text-sm">{lead.message}</p>
              </div>
            ) : null}

            <details className="text-sm">
              <summary className="text-muted-foreground cursor-pointer">
                {t('crm.leads.detail.rawPayload')}
              </summary>
              <pre className="bg-muted mt-2 overflow-x-auto rounded-md p-3 text-xs">
                {JSON.stringify(lead.payload, null, 2)}
              </pre>
            </details>

            {lead.status !== 'converted' ? (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="font-medium">{t('crm.leads.convertDialog.title')}</h3>

                  <div className="space-y-1.5">
                    <Label>{t('crm.leads.convertDialog.existingAccount')}</Label>
                    <Select value={accountId} onValueChange={setAccountId}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NEW_ACCOUNT}>
                          {t('crm.leads.convertDialog.newAccount')}
                        </SelectItem>
                        {accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {accountId === NEW_ACCOUNT ? (
                    <div className="space-y-1.5">
                      <Label>{t('crm.leads.convertDialog.accountName')}</Label>
                      <Input
                        value={accountName}
                        onChange={(event) => setAccountName(event.target.value)}
                      />
                    </div>
                  ) : null}

                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={createContact}
                      onCheckedChange={(value) => setCreateContact(value === true)}
                    />
                    {t('crm.leads.convertDialog.createContact')}
                  </label>

                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={createDeal}
                      onCheckedChange={(value) => setCreateDeal(value === true)}
                    />
                    {t('crm.leads.convertDialog.createDeal')}
                  </label>

                  {createDeal ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>{t('crm.leads.convertDialog.dealName')}</Label>
                        <Input value={dealName} onChange={(event) => setDealName(event.target.value)} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>{t('crm.leads.convertDialog.dealValue')}</Label>
                        <Input
                          value={dealValue}
                          inputMode="decimal"
                          onChange={(event) => setDealValue(event.target.value)}
                        />
                      </div>
                    </div>
                  ) : null}

                  <Button
                    onClick={handleConvert}
                    disabled={
                      submitting || (accountId === NEW_ACCOUNT && accountName.trim() === '')
                    }
                  >
                    {t('crm.leads.convertDialog.submit')}
                  </Button>
                </div>
              </>
            ) : null}

            {actions?.canRestore && (lead.rejectedReasonCode || lead.rejectedReason) ? (
              <div className="bg-muted rounded-md px-3 py-2 text-sm">
                <span className="text-muted-foreground">{t('crm.leads.reasonLabel')}: </span>
                {lead.rejectedReasonCode ? t(`crm.leadRejectReason.${lead.rejectedReasonCode}`) : '—'}
                {lead.rejectedReason ? (
                  <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{lead.rejectedReason}</p>
                ) : null}
              </div>
            ) : null}

            <Separator />
            <div className="flex flex-wrap gap-2">
              {actions?.canHide ? (
                <Button variant="outline" size="sm" onClick={() => onHide(lead.id)}>
                  <EyeOff className="mr-2 h-4 w-4" />
                  {t('crm.leads.hide')}…
                </Button>
              ) : null}
              {actions?.canRestore ? (
                <Button variant="outline" size="sm" onClick={() => onRestore(lead.id)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {t('crm.leads.restore')}
                </Button>
              ) : null}
              {actions?.canDelete ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => onDelete(lead.id)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('common.delete')}…
                </Button>
              ) : null}
            </div>

            <Separator />
            <div>
              <Label className="text-muted-foreground text-xs uppercase">
                {t('crm.leads.detail.timeline')}
              </Label>
              <ul className="mt-2 space-y-2">
                {lead.activities.map((entry) => (
                  <li key={entry.id} className="text-sm">
                    <span className="text-muted-foreground">
                      {dayjs(entry.occurredAt).format('DD.MM.YYYY HH:mm')} ·{' '}
                      {t(`crm.activityType.${entry.type}`)}
                    </span>
                    {entry.subject ? <div>{entry.subject}</div> : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
